import { useEffect } from 'react';

import loadScript from '../../utils/loadScript/loadScript';
import shouldForceClickTrackInDev from '../../utils/shouldForceClickTrackInDev/shouldForceClickTrackInDev';
import useURLParam, { OverlayURLParam } from '../useURLParam/useURLParam';

/**
 * Loads RentCafe's ClickTrack tracker for tours embedded by RentCafe, which
 * identify themselves with a `pid` (RentCafe property id) query param.
 */
const useClickTrack = (analyticsEnabled: boolean) => {
  const propertyId = useURLParam(OverlayURLParam.PID);
  const debug = useURLParam(OverlayURLParam.CLICK_TRACK_DEBUG) === 'true';
  const enabled = analyticsEnabled || shouldForceClickTrackInDev(debug);

  useEffect(() => {
    const src = window._env.CLICK_TRACK_SCRIPT_URL;

    if (!enabled || !propertyId) {
      return;
    }

    if (!src) {
      if (debug) {
        console.warn('[ClickTrack] CLICK_TRACK_SCRIPT_URL is not configured');
      }

      return;
    }

    // RentCafe requires the tracker in the document head, and initializing it
    // twice would double count every event.
    if (document.head.querySelector(`script[src="${src}"]`)) {
      return;
    }

    if (debug) {
      console.log('[ClickTrack] loading tracker', src);
    }

    loadScript(src, document.head)
      .then(() => {
        window.ClickTrack?.init();

        if (debug) {
          console.log('[ClickTrack] tracker initialized', src);
        }
      })
      .catch(() => {
        if (debug) {
          console.warn('[ClickTrack] tracker failed to load', src);
        }
      });
  }, [debug, enabled, propertyId]);
};

export default useClickTrack;
